import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { getPresignedUploadUrl, uploadToS3, getPresignedDownloadUrl, deleteAttachment, updateAttachmentLabel } from '../helpers/api';
import notesStore from '../stores/NotesStore';
import { fmt } from '../helpers/helpers';

const AttachmentsModal = observer(({ entryId, entry: passedEntry, onClose, readOnly = false }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  // renaming: { s3Key, value } | null
  const [renaming, setRenaming] = useState(null);

  const store = notesStore;
  // Use passed entry (for shared notes) or find from store
  const entry = passedEntry || store.entries.find(e => e.id === entryId);
  const attachments = entry?.attachments || [];

  // Check if user is allowed to upload (not a guest and not readonly)
  const canUpload = !store.isGuest && !readOnly;

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear any previous errors
    setError('');

    // Validate file size (e.g., max 10MB)
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      setError('Arquivo muito grande. Tamanho máximo: 10MB');
      return;
    }

    // Validate file type (allow images, PDFs and CSVs). Browsers report CSV
    // inconsistently (text/csv, application/vnd.ms-excel on Windows, or empty),
    // so CSV is also accepted by extension.
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'text/csv'];
    const isCsv = /\.csv$/i.test(file.name);
    if (!allowedTypes.includes(file.type) && !isCsv) {
      setError('Tipo de arquivo não permitido. Use JPG, PNG, PDF ou CSV.');
      return;
    }
    const fileType = isCsv ? 'text/csv' : file.type;

    setUploading(true);

    try {
      // Get presigned URL from API with noteId and entryId for organized S3 path
      const { url: presignedUrl, s3Key } = await getPresignedUploadUrl(
        store.userId,
        store.currentNoteId,
        entryId,
        file.name,
        fileType
      );

      // Upload to S3
      await uploadToS3(presignedUrl, file, fileType);

      // Save attachment metadata to store
      const attachmentData = {
        s3Key,
        filename: file.name,
        fileType,
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      };

      store.addAttachment(store.currentNoteId, entryId, attachmentData);
      store.autoAddAttachmentToShares(store.currentNoteId, entryId).catch(() => {});

      // Clear the file input
      event.target.value = '';
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Erro ao fazer upload do arquivo. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (attachment) => {
    try {
      // Get presigned download URL
      const { url } = await getPresignedDownloadUrl(store.userId, attachment.s3Key);

      // Open in new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
      setError('Erro ao abrir arquivo. Tente novamente.');
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Excluir ${attachment.filename}?`)) return;

    try {
      // Delete from S3
      await deleteAttachment(store.userId, attachment.s3Key);

      // Remove from store
      store.removeAttachment(store.currentNoteId, entryId, attachment.s3Key);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      setError('Erro ao excluir arquivo. Tente novamente.');
    }
  };

  const handleRenameSubmit = async (attachment) => {
    if (!renaming) return;
    const trimmed = renaming.value.trim();
    try {
      await updateAttachmentLabel(store.userId, store.currentNoteId, entryId, attachment.s3Key, trimmed || null);
      store.updateAttachmentLabel(store.currentNoteId, entryId, attachment.s3Key, trimmed || null);
    } catch (err) {
      setError('Erro ao renomear arquivo.');
    }
    setRenaming(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('pt-BR') + ' ' +
           date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="attachments-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>
              Anexos
              {entry?.label && ` - ${entry.label}`}
              {entry?.value !== undefined && ` (${fmt(Math.abs(entry.value))})`}
            </h3>
            <button className="close-btn" onClick={onClose} title="Fechar">
              ×
            </button>
          </div>

          <div className="modal-content">
            {/* File upload section - hide entirely for readonly */}
            {!readOnly && (
              canUpload ? (
                <div className="upload-section">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.csv,text/csv"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    hidden
                  />
                  <button
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <i className={uploading ? "fas fa-spinner fa-spin" : "fas fa-paperclip"}></i>
                    {uploading ? ' Enviando...' : ' Adicionar arquivo'}
                  </button>
                  <span className="upload-hint">
                    JPG, PNG ou PDF (máx 10MB)
                  </span>
                </div>
              ) : (
                <div className="guest-notice">
                  <i className="fas fa-exclamation-triangle"></i> Faça login para adicionar anexos
                </div>
              )
            )}

            {/* Error message */}
            {error && (
              <div className="error-message">
                <i className="fas fa-times-circle"></i> {error}
              </div>
            )}

            {/* Attachments list */}
            <div className="attachments-list">
              {attachments.length === 0 ? (
                <div className="no-attachments">
                  Nenhum anexo ainda
                </div>
              ) : (
                <ul>
                  {attachments.map((attachment, index) => (
                    <li key={attachment.s3Key || index} className="attachment-item">
                      <div className="attachment-info">
                        <i className={`attachment-icon ${
                          attachment.fileType?.startsWith('image/')
                            ? 'fas fa-image'
                            : attachment.fileType === 'application/pdf'
                            ? 'fas fa-file-pdf'
                            : attachment.fileType === 'text/csv'
                            ? 'fas fa-file-csv'
                            : 'fas fa-file'
                        }`}></i>
                        <div className="attachment-details">
                          {renaming?.s3Key === attachment.s3Key ? (
                            <div className="attachment-rename-row">
                              <input
                                autoFocus
                                className="attachment-rename-input"
                                value={renaming.value}
                                onChange={e => setRenaming({ ...renaming, value: e.target.value })}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleRenameSubmit(attachment);
                                  if (e.key === 'Escape') setRenaming(null);
                                }}
                              />
                              <button className="rename-save-btn" onClick={() => handleRenameSubmit(attachment)} title="Salvar">
                                <i className="fas fa-check"></i>
                              </button>
                              <button className="rename-cancel-btn" onClick={() => setRenaming(null)} title="Cancelar">
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <div
                              className="attachment-name"
                              title={attachment.displayName ? `Arquivo original: ${attachment.filename}` : undefined}
                            >
                              {attachment.displayName || attachment.filename}
                              {attachment.displayName && (
                                <span className="attachment-original-name"> ({attachment.filename})</span>
                              )}
                            </div>
                          )}
                          <div className="attachment-meta">
                            {formatFileSize(attachment.fileSize || 0)} • {formatDate(attachment.uploadedAt)}
                          </div>
                        </div>
                      </div>

                      <div className="attachment-actions">
                        <button
                          className="view-btn"
                          onClick={() => handleView(attachment)}
                          title="Visualizar"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {canUpload && renaming?.s3Key !== attachment.s3Key && (
                          <button
                            className="rename-btn"
                            onClick={() => setRenaming({ s3Key: attachment.s3Key, value: attachment.displayName || attachment.filename || '' })}
                            title="Renomear"
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                        )}
                        {canUpload && (
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(attachment)}
                            title="Excluir"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button className="done-btn" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

export default AttachmentsModal;